import {
  AutocompleteGenericEntity,
  SingleAutocomplete,
  SingleAutocompleteProps,
} from '@australiangreens/ag-internal-components';
import { AutocompleteChangeReason } from '@mui/material';

import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { DEFAULT_PLACEHOLDER_FIELD_LABEL } from '@pages/AdvancedSearchListCreatorPage/qbRulePropertiesMapping/util';
import { SyntheticEvent, useState } from 'react';
import { createTemplatePlaceholder } from '@/pages/AdvancedSearchListCreatorPage/customWidgets/util';
import TemplatePlaceholderContextMenu, {
  CloseOutcome,
  ContextMenuState,
  handleOpenPlaceholderContextMenu,
} from './TemplatePlaceholderContextMenu';
import { PLACEHOLDER_BLUE } from './colours';

export type TemplateCapableSingleAutocompleteChangeReason =
  | AutocompleteChangeReason
  | 'setIsTemplatePlaceholder'
  | 'unsetIsTemplatePlaceholder';

export type TemplateCapableSingleAutocompleteProps<T extends AutocompleteGenericEntity> = Omit<
  SingleAutocompleteProps<T>,
  'value' | 'onChange'
> & {
  value: SingleAutocompleteProps<T>['value'] | TemplatePlaceholder;
  onChange: (
    newValue: SingleAutocompleteProps<T>['value'] | TemplatePlaceholder,
    reason: TemplateCapableSingleAutocompleteChangeReason
  ) => void;

  isOnTemplatePage?: boolean;
  templateBuilderDisplayName?: string;
};

export default function TemplateCapableSingleAutocomplete<T extends AutocompleteGenericEntity>({
  value,
  isOnTemplatePage = false,
  onChange,
  templateBuilderDisplayName = '',
  ...props
}: TemplateCapableSingleAutocompleteProps<T>) {
  const [placeholderMenuState, setPlaceholderMenuState] = useState<ContextMenuState>(null);

  const valueIsPlaceholder = isTemplatePlaceholder(value);

  const placeholderProps = valueIsPlaceholder
    ? {
        // The following gives it the placeholder styling
        textFieldColor: 'info' as SingleAutocompleteProps<T>['textFieldColor'],
        placeholderText: DEFAULT_PLACEHOLDER_FIELD_LABEL,
        textFieldFocused: true,
        textFieldSx: {
          '& .MuiFilledInput-root.Mui-focused': {
            backgroundColor: PLACEHOLDER_BLUE,
          },
          '& .MuiOutlinedInput-root.Mui-focused': {
            backgroundColor: PLACEHOLDER_BLUE,
          },
          caretColor: 'transparent',
        },
        readOnly: true,
        hideInputEndAdornment: true,
      }
    : undefined;

  const handleNormalChange = (
    _event: SyntheticEvent<Element, Event>,
    newValue: T | null,
    reason: AutocompleteChangeReason
  ) => {
    onChange(newValue, reason);
  };

  const handleCloseTemplatePlaceholderContextMenu = (reason: CloseOutcome) => {
    setPlaceholderMenuState(null);
    if (reason === 'setIsTemplatePlaceholder') {
      onChange(createTemplatePlaceholder(templateBuilderDisplayName), 'setIsTemplatePlaceholder');
    } else if (reason === 'unsetIsTemplatePlaceholder') {
      onChange(null, 'unsetIsTemplatePlaceholder');
    }
  };

  if (isOnTemplatePage) {
    return (
      <>
        <SingleAutocomplete
          disableDefaultRightClickBehaviour
          onRightClick={(event) => {
            handleOpenPlaceholderContextMenu(event, placeholderMenuState, setPlaceholderMenuState);
          }}
          value={valueIsPlaceholder ? null : value}
          onChange={handleNormalChange}
          {...placeholderProps}
          {...props}
        />
        <TemplatePlaceholderContextMenu
          menuState={placeholderMenuState}
          placeholderActive={valueIsPlaceholder}
          onClose={handleCloseTemplatePlaceholderContextMenu}
        />
      </>
    );
  }

  if (valueIsPlaceholder) {
    throw new Error(
      'TemplateCapableSingleAutocomplete: value is a placeholder but isOnTemplatePage is false'
    );
  }

  return <SingleAutocomplete value={value} onChange={handleNormalChange} {...props} />;
}
